/* =========================================================================
 * IBC 2.0 — CART
 * -------------------------------------------------------------------------
 * The one cart controller. It drives the drawer (sections/ibc-cart-drawer)
 * and the cart page (sections/main-cart) from the same code, because the two
 * surfaces render the same snippets.
 *
 * FOUR RULES THIS FILE OBEYS
 * -------------------------
 * 1. LIQUID IS THE SOURCE OF TRUTH. Nothing is rebuilt from JS state. After
 *    every mutation the affected sections are re-fetched through the Section
 *    Rendering API and their `[data-ibc-cart-refresh]` contents swapped in.
 *
 * 2. ADDRESSING IS BY LINE KEY, NEVER BY INDEX. `/cart/change.js` re-keys a
 *    line the moment its properties change and can reorder the basket, so
 *    every call sends `id: "<line key>"`. The live theme baked 1-based
 *    `forloop.index` values into the markup and then fired two sequential
 *    change.js calls with both indices computed before the first — which
 *    could edit the wrong product (audit B, R3).
 *
 * 3. `/cart/change.js` REPLACES THE WHOLE PROPERTIES OBJECT. Every write
 *    therefore re-reads the line from a fresh `/cart.js` (or from the cart
 *    returned by the previous call), clones its CURRENT properties, mutates
 *    only the keys it owns, and sends the clone back in full. Gift-box
 *    properties survive an add-on edit; add-on properties survive a message
 *    edit. The live theme's edit path cloned a Liquid snapshot baked in at
 *    page render and silently reverted anything changed since (R2).
 *
 * 4. THE BUNDLE JOIN IS THE KEY, NOTHING ELSE. Parent `Linked add-on key`
 *    string-equals child `Linked cake key`. Never a product id, never a line
 *    index — two cakes with different add-ons must not cross-associate.
 *
 * `_bundle_removed` (contracts §4.4) is Shopify's empty-properties
 * workaround, not business data: deleted before every rebuild, and re-added
 * only if the rebuilt object would otherwise be `{}`, which change.js
 * rejects.
 *
 * Events: dispatches `ibc:cart-changed` on `document` after its own
 * mutations (with `detail.source === 'cart'`) so the header bubble keeps up,
 * and listens for the same event from `ibc-product.js`, `ibc-addons.js` and
 * `ibc-quick-add.js` to refresh and open the drawer after an add to cart.
 *
 * Depends on: assets/ibc-dialog.js (custom element `ibc-dialog`).
 * ====================================================================== */
(function () {
  'use strict';

  /* RE-ENTRY GUARD. `sections/main-cart.liquid` and
   * `sections/ibc-cart-drawer.liquid` both emit this file, so on /cart the
   * browser executes it twice — one fetch, two <script> tags, two runs. The
   * second run registered a SECOND document-level click listener, so every
   * [data-ibc-edit-toggle] click ran the handler twice: open, then straight
   * back closed. That made the "add a personalised message" prompt, the
   * Edit control on a personalised message and the Edit control on the
   * chocolate mix all inert on the cart page.
   *
   * Same idiom as assets/ibc-tile.js and assets/ibc-quickview.js: the
   * exported handle at the foot of this file is the flag. */
  if (window.IbcCart) return;

  /* ==================================================================== */
  /* CONTRACT                                                             */
  /* ==================================================================== */

  /* Verbatim from deliverables/SHOPIFY_DATA_CONTRACTS.md §4. The casing
   * split between the parent's `Triple Chocolate Choice` and the child's
   * `Triple chocolate choice` is deliberate and load-bearing: both spellings
   * are read defensively, and normalising only one silently empties the
   * customer's chocolate mix on the order. */
  var P = {
    /* PREFIXED WITH `_` SO CHECKOUT DOES NOT PRINT THEM. Shopify keeps a
     * private property on the cart, on the order and in the admin, and
     * leaves it out of checkout, the confirmation email and the order status
     * page. See the long note in assets/ibc-addons.js, which is the writer.
     *
     * `choiceChild` and `personalisedMessage` are the two that stay public:
     * they are the customer's own words and choices, not plumbing. */
    tripleChocolate: '_Triple Chocolate',
    tripleChocolateAddOn: '_Triple Chocolate Add-on',
    choiceParent: '_Triple Chocolate Choice',
    choiceChild: 'Triple chocolate choice',
    chocolateSize: '_Triple chocolate size',
    linkedAddOnKey: '_Linked add-on key',
    linkedCakeKey: '_Linked cake key',
    personalisedMessage: 'Personalised message',
    addedToCake: '_Added to cake',
    cakeProduct: '_Cake product',
    cakeVariant: '_Cake variant',
    cakeSku: '_Cake SKU',
    cakeVariantId: '_Cake variant ID',
    bundleRemoved: '_bundle_removed',

    /* THE MULTIPACK, which is N lines rather than one. Every chosen piece is
     * its own cart line at its own variant and its own real price — Shopify
     * prices a line by its variant, and nothing in a theme can make one line
     * cost the sum of things named in its properties. The lines that belong
     * to one pack all carry the same `packId`, which is what draws them
     * together here and in snippets/ibc-cart-lines.liquid, and each carries
     * `packUnit`: how many of THAT piece are in ONE pack. */
    packId: '_Selection ID',
    packUnit: '_Selection Unit',
    /* THE BUNDLE'S OWN JOIN, the same idea under a different name. A bundle
     * is a GBP 0 header line plus one line per chocolate, all sharing
     * `_Bundle Group` — see assets/ibc-bundle.js. Every group behaviour below
     * is identical to a pack's, so rather than a second set of functions the
     * two lookups simply fall back to these. The header carries no
     * `_Bundle Unit`, which is right: one header per bundle, so its unit is
     * the 1 that `packUnitOf` already falls back to. */
    groupId: '_Bundle Group',
    groupUnit: '_Bundle Unit',
    packHandle: '_Selection Handle',
    packName: 'Selection'
  };

  /* THE SPELLINGS USED BEFORE THE `_` PREFIX, kept for one reason: a cart
   * created before that change is still in somebody's browser. Shopify
   * carts survive for weeks, so for weeks after this ships there will be
   * baskets whose add-on lines say `Linked cake key` rather than
   * `_Linked cake key`.
   *
   * Reading only the new name would un-nest those baskets — every add-on
   * would float to the top level as its own line, and removing a cake would
   * leave its message behind. So every READ goes through `readProp` and
   * every DELETE through `dropProp`, both of which try the new name and then
   * the old one. WRITES use `P` alone: nothing new is ever written with an
   * old name, so a basket heals itself the first time it is touched.
   *
   * Only the keys that actually changed are listed. Anything absent here is
   * spelled the same either way and needs no fallback. */
  var LEGACY = {
    tripleChocolate: 'Triple Chocolate',
    tripleChocolateAddOn: 'Triple Chocolate Add-on',
    choiceParent: 'Triple Chocolate Choice',
    chocolateSize: 'Triple chocolate size',
    linkedAddOnKey: 'Linked add-on key',
    linkedCakeKey: 'Linked cake key',
    addedToCake: 'Added to cake',
    cakeProduct: 'Cake product',
    cakeVariant: 'Cake variant',
    cakeSku: 'Cake SKU',
    cakeVariantId: 'Cake variant ID'
  };

  /* The current value under either spelling. An empty string counts as
   * absent, because that is how Shopify represents a property that has been
   * cleared rather than removed. */
  function readProp(properties, name) {
    if (!properties) return undefined;
    var value = properties[P[name]];
    if (value === undefined || value === null || value === '') {
      if (LEGACY[name]) value = properties[LEGACY[name]];
    }
    return value;
  }

  /* Remove BOTH spellings. A rebuild that deleted only the new one would
   * leave the old copy behind, and the parent line would keep an add-on
   * summary for a child that is no longer in the basket. */
  function dropProp(properties, name) {
    delete properties[P[name]];
    if (LEGACY[name]) delete properties[LEGACY[name]];
  }

  var DRAWER_ID = 'IbcCartDrawer';

  var MSG = {
    generic: 'Sorry, your basket could not be updated. Please try again.',
    gone: 'That item is no longer in your basket.',
    already: 'That extra is already on this item.',
    noFlavour: 'Select at least one chocolate type.',
    one: 'Choose one chocolate type.',
    upTo: function (n) {
      return 'Choose up to ' + n + ' chocolate types.';
    },
    noMessage: 'Enter a message, or remove the line instead.',
    tooLong: function (n) {
      return 'Keep the message to ' + n + ' characters or fewer.';
    },
    noteSaved: 'Note saved.',
    noteFailed: 'Your note could not be saved.'
  };

  /* ==================================================================== */
  /* SMALL UTILITIES                                                      */
  /* ==================================================================== */

  function toArray(list) {
    return Array.prototype.slice.call(list || []);
  }

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function clone(object) {
    var out = {};
    if (!object) return out;
    Object.keys(object).forEach(function (name) {
      out[name] = object[name];
    });
    return out;
  }

  function reducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function post(path, body) {
    return fetch(root() + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (response) {
      return response
        .json()
        .catch(function () {
          return {};
        })
        .then(function (data) {
          if (!response.ok) {
            throw new Error(data.description || data.message || MSG.generic);
          }
          return data;
        });
    });
  }

  function getCart() {
    return fetch(root() + 'cart.js', { headers: { Accept: 'application/json' } }).then(function (
      response
    ) {
      if (!response.ok) throw new Error(MSG.generic);
      return response.json();
    });
  }

  /* ==================================================================== */
  /* BUNDLE HELPERS — the key join, and nothing but the key join          */
  /* ==================================================================== */

  function byKey(cart, key) {
    var items = (cart && cart.items) || [];
    for (var i = 0; i < items.length; i++) {
      if (items[i].key === key) return items[i];
    }
    return null;
  }

  function parentFor(cart, bundleKey) {
    var items = (cart && cart.items) || [];
    for (var i = 0; i < items.length; i++) {
      var properties = items[i].properties || {};
      if (readProp(properties, 'linkedAddOnKey') === bundleKey) return items[i];
    }
    return null;
  }

  function childrenFor(cart, bundleKey) {
    return ((cart && cart.items) || []).filter(function (item) {
      return readProp(item.properties || {}, 'linkedCakeKey') === bundleKey;
    });
  }

  /* EVERY LINE OF ONE PACK, in cart order. The join is the pack id and
   * nothing else: not the product, not the handle, not the position. Two
   * packs of the same product sharing a piece therefore never merge, because
   * their pack ids differ and Shopify only merges lines whose variant AND
   * properties both match. */
  function packMembers(cart, packId) {
    if (!packId) return [];
    return ((cart && cart.items) || []).filter(function (item) {
      var props = item.properties || {};
      /* EITHER JOIN. A pack's `_Selection ID` or a bundle's `_Bundle Group`:
       * both mean "these lines move together", and the ids are opaque enough
       * that one can never collide with the other. A bundle's header line
       * carries the group too, so a Remove takes the header with it and a
       * quantity change scales it — which is what makes one bundle one
       * header rather than a stray GBP 0 line left behind. */
      return props[P.packId] === packId || props[P.groupId] === packId;
    });
  }

  /* How many of a piece are in ONE pack. Falls back to 1 rather than to the
   * line quantity: a missing unit must never silently multiply a basket. */
  function packUnitOf(item) {
    var props = item.properties || {};
    var unit = parseInt(props[P.packUnit], 10);
    if (!unit || unit < 1) unit = parseInt(props[P.groupUnit], 10);
    return !unit || unit < 1 ? 1 : unit;
  }

  /* A chocolate add-on carries a size or a mix; the personalised-message
   * line deliberately carries neither (contracts §4.2). */
  function isChocolateChild(item) {
    var properties = item.properties || {};
    return !!(
      readProp(properties, 'chocolateSize') ||
      readProp(properties, 'choiceChild') ||
      readProp(properties, 'choiceParent')
    );
  }

  /* The same generator `assets/ibc-addons.js` uses on the product page, so a
   * bundle created from the basket is indistinguishable from one created on
   * the PDP: `${handle}-${variantId}-${Date.now()}`. */
  function newBundleKey(line) {
    return String(line.handle || 'product') + '-' + String(line.variant_id) + '-' + Date.now();
  }

  /* Rebuild a parent's derived add-on summary from the children that are
   * ACTUALLY in the basket right now. Everything unrelated — gift box,
   * uploads, anything an app wrote — is left untouched because `properties`
   * arrives here as a clone of the live line.
   *
   * The delete/rebuild/re-add dance around `_bundle_removed` is contracts
   * §4.4 exactly: delete it first, re-add it last only if the object would
   * otherwise be empty. */
  function rebuildParent(properties, children, bundleKey) {
    dropProp(properties, 'tripleChocolate');
    dropProp(properties, 'tripleChocolateAddOn');
    dropProp(properties, 'choiceParent');
    dropProp(properties, 'choiceChild');
    dropProp(properties, 'personalisedMessage');
    dropProp(properties, 'linkedAddOnKey');
    dropProp(properties, 'bundleRemoved');

    var chocolate = children.filter(isChocolateChild);
    var message = '';
    children.forEach(function (child) {
      var childProperties = child.properties || {};
      if (!message && !isChocolateChild(child) && readProp(childProperties, 'personalisedMessage')) {
        message = readProp(childProperties, 'personalisedMessage');
      }
    });

    if (chocolate.length) {
      properties[P.tripleChocolate] = 'Yes';
      /* `<title> - <variant title>`, the form assets/ibc-addons.js writes on
       * the product page. The live cart omitted the variant segment when it
       * was 'Default Title', so the same bundle produced two different
       * strings depending on where it was built (contracts §4.1). One form
       * here, always. */
      properties[P.tripleChocolateAddOn] = chocolate
        .map(function (child) {
          var childProperties = child.properties || {};
          var title = child.product_title || child.title || 'Add-on';
          /* readProp, NOT a direct lookup. A child from a pre-rename basket
           * carries `Triple chocolate size`, and reading only the private
           * name fell through to `variant_title` — which on a single-variant
           * add-on is 'Default Title', so the parent's summary read
           * "Triple Chocolate for Whole Cake - Default Title" instead of
           * naming the size. Caught by test-private.mjs, not by reading. */
          var variantTitle =
            readProp(childProperties, 'chocolateSize') || child.variant_title || 'Default Title';
          return title + ' - ' + variantTitle;
        })
        .join(' + ');

      var choice = chocolate
        .map(function (child) {
          var childProperties = child.properties || {};
          return readProp(childProperties, 'choiceChild') ||
            readProp(childProperties, 'choiceParent') || '';
        })
        .filter(Boolean)
        .join(' + ');
      if (choice) properties[P.choiceParent] = choice;
    }

    if (message) properties[P.personalisedMessage] = message;
    if (children.length) properties[P.linkedAddOnKey] = bundleKey;

    /* Shopify rejects an empty properties object on change.js. */
    if (!Object.keys(properties).length) properties[P.bundleRemoved] = 'true';

    return properties;
  }

  /* ==================================================================== */
  /* SECTION RENDERING — Liquid stays the source of truth                 */
  /* ==================================================================== */

  function sectionHosts() {
    return toArray(document.querySelectorAll('[data-ibc-cart-section]'));
  }

  /* Remember enough about the focused control to put focus back on the same
   * control after its section has been replaced. */
  var FOCUS_MARKERS = [
    'data-ibc-qty-step',
    'data-ibc-qty-input',
    'data-ibc-cart-remove',
    'data-ibc-edit-toggle',
    'data-ibc-addon-add',
    'data-ibc-cart-note'
  ];

  function focusToken() {
    var active = document.activeElement;
    if (!active || !active.getAttribute) return null;
    for (var i = 0; i < FOCUS_MARKERS.length; i++) {
      if (active.hasAttribute(FOCUS_MARKERS[i])) {
        var line = active.closest ? active.closest('[data-ibc-cart-line]') : null;
        /* A PACK'S OWN CONTROLS SIT IN ITS HEADER, outside every line, so
         * there is no line key to scope the restore by. Without the pack
         * recorded here `restoreFocus` would fall back to `document` and put
         * focus on the FIRST matching control in the basket — somebody
         * else's Remove button. */
        var pack = active.closest ? active.closest('[data-ibc-cart-pack]') : null;
        return {
          marker: FOCUS_MARKERS[i],
          value: active.getAttribute(FOCUS_MARKERS[i]),
          key: line ? line.getAttribute('data-line-key') : null,
          pack: line || !pack ? null : pack.getAttribute('data-ibc-cart-pack')
        };
      }
    }
    return null;
  }

  function restoreFocus(token) {
    if (!token) return;
    var scope = document;
    if (token.key) {
      scope = document.querySelector('[data-line-key="' + token.key.replace(/"/g, '\\"') + '"]');
    } else if (token.pack) {
      scope = document.querySelector(
        '[data-ibc-cart-pack="' + token.pack.replace(/"/g, '\\"') + '"]'
      );
    }
    if (!scope) {
      var panel = document.querySelector('#' + DRAWER_ID + ' .dialog__panel');
      if (panel) panel.focus();
      return;
    }
    var selector = '[' + token.marker + (token.value ? '="' + token.value + '"' : '') + ']';
    var target = scope.querySelector ? scope.querySelector(selector) : null;
    if (target && target.focus) target.focus();
  }

  function refresh() {
    var hosts = sectionHosts();
    if (!hosts.length) return Promise.resolve();

    var ids = hosts.map(function (host) {
      return host.getAttribute('data-ibc-cart-section');
    });
    /* ALWAYS request the sections from the shop root, and NEVER send
     * `Accept: application/json`.
     *
     * Shopify special-cases `GET /cart` with `Accept: application/json` and
     * answers with the CART OBJECT rather than the Section Rendering
     * payload. `refresh()` then finds keys like `token` and `items`, matches
     * no `[data-ibc-cart-section]` host, swaps nothing in and raises no
     * error — so the cart page never re-renders and the stale quantity in
     * the DOM makes the next `-` send 0 and delete the whole bundle.
     * See QA D-03. The Section Rendering API returns JSON either way. */
    var base = root();
    var url =
      base +
      (base.indexOf('?') === -1 ? '?' : '&') +
      'sections=' +
      ids.map(encodeURIComponent).join(',');

    var token = focusToken();
    var noteField = document.querySelector('[data-ibc-cart-note]');
    var noteDraft = noteField && document.activeElement === noteField ? noteField.value : null;

    return fetch(url)
      .then(function (response) {
        if (!response.ok) throw new Error(MSG.generic);
        return response.json();
      })
      .then(function (data) {
        var parser = new DOMParser();
        Object.keys(data).forEach(function (id) {
          var selector = '[data-ibc-cart-section="' + id.replace(/"/g, '\\"') + '"]';
          var host = document.querySelector(selector);
          if (!host) return;
          var fresh = parser.parseFromString(data[id], 'text/html').querySelector(selector);
          if (!fresh) return;
          host.innerHTML = fresh.innerHTML;
          host.setAttribute('data-cart-count', fresh.getAttribute('data-cart-count') || '0');
        });

        if (noteDraft !== null) {
          var field = document.querySelector('[data-ibc-cart-note]');
          if (field) field.value = noteDraft;
        }
        restoreFocus(token);
        announceState();
      });
  }

  /* ==================================================================== */
  /* STATUS, ERRORS, BUSY                                                 */
  /* ==================================================================== */

  var busy = false;

  function setBusy(state, line) {
    busy = state;
    toArray(document.querySelectorAll('[data-ibc-cart-busy]')).forEach(function (element) {
      element.hidden = !state;
    });
    if (line) line.classList.toggle('is-busy', state);
    document.documentElement.style.cursor = state ? 'progress' : '';
  }

  function announce(message) {
    toArray(document.querySelectorAll('[data-ibc-cart-live]')).forEach(function (element) {
      element.textContent = message;
    });
  }

  function announceState() {
    var host = document.querySelector('[data-ibc-cart-section]');
    var count = host ? Number(host.getAttribute('data-cart-count') || 0) : 0;
    var subtotal = document.querySelector('[data-ibc-cart-subtotal]');
    announce(
      'Basket updated. ' +
        count +
        ' item' +
        (count === 1 ? '' : 's') +
        (subtotal ? '. Subtotal ' + subtotal.textContent.trim() + '.' : '.')
    );
  }

  function showError(scope, message) {
    var slot =
      (scope && scope.querySelector('[data-ibc-line-error]')) ||
      document.querySelector('[data-ibc-cart-error]');
    if (slot) slot.textContent = message;
    announce(message);
  }

  function clearErrors() {
    toArray(document.querySelectorAll('[data-ibc-line-error], [data-ibc-cart-error]')).forEach(
      function (element) {
        element.textContent = '';
      }
    );
  }

  /* One re-entrancy guard for every mutation. `task` returns a promise that
   * resolves with the mutated cart. */
  function run(line, task) {
    if (busy) return Promise.resolve();
    clearErrors();
    setBusy(true, line);
    return Promise.resolve()
      .then(task)
      .then(function (cart) {
        return refresh().then(function () {
          if (cart && typeof cart.item_count === 'number') {
            document.dispatchEvent(
              new CustomEvent('ibc:cart-changed', {
                bubbles: true,
                detail: {
                  cart: cart,
                  item_count: cart.item_count,
                  items: cart.items,
                  total_price: cart.total_price,
                  source: 'cart'
                }
              })
            );
          }
        });
      })
      .catch(function (error) {
        showError(line, (error && error.message) || MSG.generic);
      })
      .then(function () {
        setBusy(false, line);
      });
  }

  /* ==================================================================== */
  /* LINE CONTEXT                                                         */
  /* ==================================================================== */

  /* TWO SCOPES, NEAREST WINS.
   *
   * A control inside a cart line resolves to that line, exactly as before —
   * `closest` walks up and meets the line first. A control in a PACK's own
   * header sits inside no line at all, so it resolves to the pack, and the
   * context it returns carries `pack` instead of `key`. Everything
   * downstream branches on `context.pack` being set.
   *
   * The pack's controls are deliberately the only ones it has: a stepper or
   * a Remove on one piece would let somebody build a two-piece three-pack,
   * which the builder itself refuses to do. */
  function contextFor(element) {
    var scope = element.closest('[data-ibc-cart-line], [data-ibc-cart-pack]');
    if (!scope) return null;

    if (!scope.hasAttribute('data-ibc-cart-line')) {
      return {
        element: scope,
        key: null,
        pack: scope.getAttribute('data-ibc-cart-pack'),
        role: 'pack',
        bundle: '',
        title: scope.getAttribute('data-pack-title') || 'this pack',
        quantity: parseInt(scope.getAttribute('data-pack-quantity'), 10) || 1
      };
    }

    return {
      element: scope,
      key: scope.getAttribute('data-line-key'),
      pack: null,
      role: scope.getAttribute('data-line-role'),
      bundle: scope.getAttribute('data-bundle-key') || '',
      title: scope.getAttribute('data-line-title') || 'this item',
      quantity: parseInt(scope.getAttribute('data-line-quantity'), 10) || 1
    };
  }

  /* ==================================================================== */
  /* MUTATIONS                                                            */
  /* ==================================================================== */

  /* Quantity. A parent and every child of its bundle move together, to the
   * same absolute quantity — the live theme's 1:1 rule, preserved. One
   * `/cart/update.js` keyed by line key, so properties are never touched. */
  function setQuantity(context, quantity) {
    return run(context.element, function () {
      return getCart().then(function (cart) {
        /* A PACK MOVES AS ONE, AND EXACTLY.
         *
         * Every member is set to its own `_Selection Unit` times the new pack
         * quantity — a multiplication from a stated fact, never a division
         * of the line quantity, so there is nothing to round and a pack of
         * `2 × Pistachio, 1 × Coffee` at three packs is 6 and 3 rather than
         * whatever a ratio happened to land on.
         *
         * ONE `/cart/update.js`, keyed by line key, so no line's properties
         * are touched and nothing is re-keyed. `rebuildParent()` is NOT
         * reachable from here and must not be: it deletes and rebuilds the
         * cake-specific keys, which a pack line does not carry. */
        if (context.pack) {
          var members = packMembers(cart, context.pack);
          if (!members.length) throw new Error(MSG.gone);

          var packUpdates = {};
          for (var i = 0; i < members.length; i++) {
            packUpdates[members[i].key] = quantity > 0 ? packUnitOf(members[i]) * quantity : 0;
          }
          return post('cart/update.js', { updates: packUpdates });
        }

        var line = byKey(cart, context.key);
        if (!line) throw new Error(MSG.gone);

        var updates = {};
        updates[context.key] = quantity;
        if (context.role === 'parent' && context.bundle) {
          childrenFor(cart, context.bundle).forEach(function (child) {
            updates[child.key] = quantity;
          });
        }
        return post('cart/update.js', { updates: updates });
      });
    });
  }

  /* Removing a child on its own. The child goes first — its own line key is
   * unaffected by anything that happens to the parent — and the parent's
   * derived summary is then rebuilt from the children that remain, using the
   * cart the removal itself returned. */
  function removeChild(context) {
    return run(context.element, function () {
      return post('cart/change.js', { id: context.key, quantity: 0 }).then(function (cart) {
        if (!context.bundle) return cart;

        var parent = parentFor(cart, context.bundle);
        /* An orphan has no parent to repair; it is simply gone. */
        if (!parent) return cart;

        var properties = rebuildParent(
          clone(parent.properties),
          childrenFor(cart, context.bundle),
          context.bundle
        );
        return post('cart/change.js', {
          id: parent.key,
          quantity: parent.quantity,
          properties: properties
        });
      });
    });
  }

  /* A pack falls through to `setQuantity(context, 0)`, which zeroes every one
   * of its lines in a single `/cart/update.js`. It must NOT take the
   * `removeChild` path: that repairs a parent's derived summary through
   * `rebuildParent()`, which is cake machinery a pack line knows nothing
   * about. */
  function removeLine(context) {
    if (context.role === 'child' || context.role === 'orphan') return removeChild(context);
    return setQuantity(context, 0);
  }

  /* Saving the chocolate mix. Both lines are rewritten in full: the child
   * gets the sentence-case spelling, and the parent's title-case summary is
   * then derived from the children that are actually in the basket, so the
   * pair can never drift. */
  function saveChoice(context, wrap) {
    var inputs = toArray(wrap.querySelectorAll('[data-ibc-choice-input]'));
    var picked = inputs
      .filter(function (input) {
        return input.checked;
      })
      .map(function (input) {
        return String(input.value || '').trim();
      })
      .filter(Boolean);
    var max = parseInt(wrap.getAttribute('data-max'), 10) || 0;
    var slot = wrap.querySelector('[data-ibc-edit-error]');

    if (!picked.length) {
      if (slot) slot.textContent = MSG.noFlavour;
      return Promise.resolve();
    }
    if (max && picked.length > max) {
      if (slot) slot.textContent = max === 1 ? MSG.one : MSG.upTo(max);
      return Promise.resolve();
    }
    if (slot) slot.textContent = '';

    var choiceText = picked.join(', ');

    return run(context.element, function () {
      return getCart()
        .then(function (cart) {
          var child = byKey(cart, context.key);
          if (!child) throw new Error(MSG.gone);

          /* Clone of the LIVE properties, so the gift box, the personalised
           * message and anything an app added all survive. */
          var properties = clone(child.properties);
          properties[P.choiceChild] = choiceText;
          dropProp(properties, 'choiceParent');

          return post('cart/change.js', {
            id: context.key,
            quantity: child.quantity,
            properties: properties
          });
        })
        .then(function (cart) {
          if (!context.bundle) return cart;
          var parent = parentFor(cart, context.bundle);
          if (!parent) return cart;

          var properties = rebuildParent(
            clone(parent.properties),
            childrenFor(cart, context.bundle),
            context.bundle
          );
          return post('cart/change.js', {
            id: parent.key,
            quantity: parent.quantity,
            properties: properties
          });
        });
    });
  }

  /* Saving the personalised message. Same shape: the child first, then the
   * parent rebuilt from the children. */
  function saveMessage(context, wrap) {
    var field = wrap.querySelector('[data-ibc-message-input]');
    var slot = wrap.querySelector('[data-ibc-edit-error]');
    var maxLength = parseInt(field && field.getAttribute('data-max-length'), 10) || 0;
    var message = field ? String(field.value || '').trim() : '';

    if (!message) {
      if (slot) slot.textContent = MSG.noMessage;
      return Promise.resolve();
    }
    if (maxLength && message.length > maxLength) {
      if (slot) slot.textContent = MSG.tooLong(maxLength);
      return Promise.resolve();
    }
    if (slot) slot.textContent = '';

    return run(context.element, function () {
      return getCart()
        .then(function (cart) {
          var child = byKey(cart, context.key);
          if (!child) throw new Error(MSG.gone);

          var properties = clone(child.properties);
          properties[P.personalisedMessage] = message;

          return post('cart/change.js', {
            id: context.key,
            quantity: child.quantity,
            properties: properties
          });
        })
        .then(function (cart) {
          if (!context.bundle) return cart;
          var parent = parentFor(cart, context.bundle);
          if (!parent) return cart;

          var properties = rebuildParent(
            clone(parent.properties),
            childrenFor(cart, context.bundle),
            context.bundle
          );
          return post('cart/change.js', {
            id: parent.key,
            quantity: parent.quantity,
            properties: properties
          });
        });
    });
  }

  /* "PAIRS WELL WITH" IS A LIST OF LINKS AND NOTHING ELSE.
   *
   * `addPair()` and the `[data-ibc-pair-add]` branch of the click delegate
   * used to live here. Every suggestion row's control — in the drawer, on
   * the cart page and now in the product page's band — is a plain VIEW link
   * to the product page (snippets/ibc-cart-pair-card.liquid), so no element
   * in the theme carries `data-ibc-pair-add` any more and neither could ever
   * be reached. Both are deleted rather than left as unreachable code, along
   * with the `[data-ibc-pairs] .ibc-pairs__title` focus move, which existed
   * only to catch focus when a pressed row vanished under the customer's
   * hands. Nothing vanishes now.
   *
   * ==================================================================== */

  /* ATTACHING AN EXTRA FROM THE BASKET.
   *
   * Three writes, in this order, and each one re-reads the line it is about
   * to change from the cart the previous call returned:
   *
   *   1. make sure the parent carries a `Linked add-on key`. A cake that
   *      already has one KEEPS IT — the second extra joins the bundle that
   *      is already there rather than starting a rival one, which is what
   *      lets a cake carry a chocolate add-on and a personalised message at
   *      the same time. This write is skipped entirely in that case;
   *   2. add the child, with the property set its KIND calls for. Contracts
   *      §4.2: a chocolate add-on carries `Triple chocolate size` and
   *      `Triple chocolate choice`; the personalised-message line carries
   *      `Personalised message` and NEITHER of those;
   *   3. rebuild the parent's derived summary from the children that are
   *      actually in the basket, through `rebuildParent()` — the same
   *      function the remove and edit paths use. That is what keeps
   *      `Triple Chocolate`, `Triple Chocolate Add-on`, the title-case
   *      `Triple Chocolate Choice` and `Personalised message` correct when
   *      there is more than one child, and it is why none of those names is
   *      written by hand here.
   *
   * DUPLICATES ARE IMPOSSIBLE FROM HERE. Liquid does not render a prompt
   * for an extra already attached to the line, and this checks the live
   * basket again before writing anything, because the page may have been
   * open for a while.
   *
   * If the child add fails and step 1 had created the key, the parent is
   * rolled back to exactly the properties it had — `_bundle_removed`
   * included, per contracts §4.4. */
  function addAddon(context, offer) {
    var variantId = Number(offer.getAttribute('data-variant-id'));
    var kind = offer.getAttribute('data-addon-kind') || 'addon';
    var hasChoice = offer.getAttribute('data-has-choice') === 'true';
    var max = parseInt(offer.getAttribute('data-max'), 10) || 0;
    var addonVariantTitle = offer.getAttribute('data-addon-variant-title') || 'Default Title';
    var slot = offer.querySelector('[data-ibc-edit-error]');
    var field = offer.querySelector('[data-ibc-offer-message]');
    var choiceText = '';
    var messageText = '';

    if (!variantId) return Promise.resolve();

    if (kind === 'message') {
      var maxLength = parseInt(field && field.getAttribute('data-max-length'), 10) || 0;
      messageText = field ? String(field.value || '').trim() : '';
      if (!messageText) {
        if (slot) slot.textContent = MSG.noMessage;
        if (field) field.focus();
        return Promise.resolve();
      }
      if (maxLength && messageText.length > maxLength) {
        if (slot) slot.textContent = MSG.tooLong(maxLength);
        if (field) field.focus();
        return Promise.resolve();
      }
    } else if (hasChoice) {
      var picked = toArray(offer.querySelectorAll('[data-ibc-offer-flavour]'))
        .filter(function (input) {
          return input.checked;
        })
        .map(function (input) {
          return String(input.value || '').trim();
        })
        .filter(Boolean);

      if (!picked.length) {
        if (slot) slot.textContent = MSG.noFlavour;
        return Promise.resolve();
      }
      if (max && picked.length > max) {
        if (slot) slot.textContent = max === 1 ? MSG.one : MSG.upTo(max);
        return Promise.resolve();
      }
      choiceText = picked.join(', ');
    }
    if (slot) slot.textContent = '';

    var bundleKey = '';
    var original = null;
    var created = false;

    return run(context.element, function () {
      return getCart()
        .then(function (cart) {
          var parent = byKey(cart, context.key);
          if (!parent) throw new Error(MSG.gone);

          var existing = (parent.properties || {})[P.linkedAddOnKey] || '';
          if (existing) {
            var already = childrenFor(cart, existing).filter(function (child) {
              return String(child.variant_id) === String(variantId);
            });
            if (already.length) throw new Error(MSG.already);
          }

          original = clone(parent.properties);

          if (existing) {
            /* The bundle is already there; nothing about the parent has to
             * change before the child can join it. */
            bundleKey = existing;
            return cart;
          }

          created = true;
          bundleKey = newBundleKey(parent);

          var properties = clone(parent.properties);
          dropProp(properties, 'bundleRemoved');
          properties[P.linkedAddOnKey] = bundleKey;

          return post('cart/change.js', {
            id: parent.key,
            quantity: parent.quantity,
            properties: properties
          });
        })
        .then(function (cart) {
          /* The parent's key changed when its properties did — find it again
           * by the bundle key, never by the old key or an index. */
          var parent = parentFor(cart, bundleKey);
          if (!parent) throw new Error(MSG.generic);

          var variantTitle = parent.variant_title || 'Default Title';
          var sku = parent.sku || '';
          var label = sku
            ? parent.product_title + ' - ' + variantTitle + ' / SKU: ' + sku
            : parent.product_title + ' - ' + variantTitle;

          var properties = {};
          properties[P.addedToCake] = label;
          properties[P.cakeProduct] = parent.product_title;
          properties[P.cakeVariant] = variantTitle;
          properties[P.cakeSku] = sku;
          properties[P.cakeVariantId] = String(parent.variant_id || '');
          properties[P.linkedCakeKey] = bundleKey;

          if (kind === 'message') {
            properties[P.personalisedMessage] = messageText;
          } else {
            properties[P.chocolateSize] = addonVariantTitle;
            properties[P.choiceChild] = choiceText;
          }

          return post('cart/add.js', {
            items: [{ id: variantId, quantity: parent.quantity, properties: properties }]
          }).catch(function (error) {
            /* Roll the parent back so a failed add never leaves a cake
             * pointing at a bundle that does not exist. Only when this call
             * created the bundle: joining an existing one wrote nothing. */
            if (!created) throw error;
            return getCart()
              .then(function (current) {
                var stranded = parentFor(current, bundleKey);
                if (!stranded) throw error;
                var rollback = clone(original);
                if (!Object.keys(rollback).length) rollback[P.bundleRemoved] = 'true';
                return post('cart/change.js', {
                  id: stranded.key,
                  quantity: stranded.quantity,
                  properties: rollback
                });
              })
              .then(function () {
                throw error;
              });
          });
        })
        .then(function () {
          /* Step 3. Every derived name on the parent comes from here, from
           * the children that are really in the basket, so a second extra
           * can never leave the summary describing only the first.
           *
           * THE BASKET IS RE-READ FIRST, and that `getCart()` is the whole
           * fix. `/cart/add.js` does NOT answer with the basket: it answers
           * with the LINE IT JUST ADDED — the line object itself for a
           * single add, `{ items: [<added lines>] }` for this array form.
           * Either way `cart.items` never contains the parent, so
           * `parentFor()` returned null, the guard below returned early, and
           * `rebuildParent()` was never reached. The child was written
           * correctly and the parent kept whatever it already had, which is
           * exactly the reported symptom: a cake carrying `Linked add-on
           * key` and the chocolate trio from its product-page add, but no
           * `Personalised message` after the basket prompt attached one.
           *
           * Nothing else had to change. The remove and edit paths were never
           * affected because `/cart/change.js` DOES answer with the whole
           * basket, which is why removal stripped the property correctly and
           * hid the asymmetry.
           *
           * Re-reading also means this branch now resolves with a real cart,
           * so `run()` can see `item_count` and fire `ibc:cart-changed` for
           * the header bubble — with the add.js payload it never could. */
          return getCart().then(function (cart) {
            var parent = parentFor(cart, bundleKey);
            if (!parent) return cart;

            var properties = rebuildParent(
              clone(parent.properties),
              childrenFor(cart, bundleKey),
              bundleKey
            );
            return post('cart/change.js', {
              id: parent.key,
              quantity: parent.quantity,
              properties: properties
            });
          });
        });
    });
  }

  /* ==================================================================== */
  /* ORDER NOTE — debounce-saved, so a quantity change cannot lose it      */
  /* ==================================================================== */

  var noteTimer = null;

  function saveNote(field) {
    var status = field.parentNode.querySelector('[data-ibc-note-status]');
    return post('cart/update.js', { note: field.value })
      .then(function () {
        if (status) status.textContent = MSG.noteSaved;
      })
      .catch(function () {
        if (status) status.textContent = MSG.noteFailed;
      });
  }

  /* ==================================================================== */
  /* DRAWER                                                               */
  /* ==================================================================== */

  function drawer() {
    return document.getElementById(DRAWER_ID);
  }

  function onCartPage() {
    return document.body.classList.contains('template-cart');
  }

  function openDrawer(opener) {
    var element = drawer();
    if (!element || typeof element.show !== 'function') return;
    element.show(opener || null);
  }

  /* ==================================================================== */
  /* EVENT WIRING — delegated once, survives every section refresh         */
  /* ==================================================================== */

  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || !target.closest) return;

    /* The header basket button opens the drawer instead of navigating.
     * On /cart itself the link is left alone — the page IS the basket. */
    var cartLink = target.closest('[data-ibc-cart-link]');
    if (cartLink && drawer() && !onCartPage()) {
      event.preventDefault();
      openDrawer(cartLink);
      return;
    }

    var step = target.closest('[data-ibc-qty-step]');
    if (step) {
      var stepContext = contextFor(step);
      if (!stepContext) return;
      var input = stepContext.element.querySelector('[data-ibc-qty-input]');
      var current = parseInt(input && input.value, 10);
      if (isNaN(current)) current = stepContext.quantity;
      var next = current + Number(step.getAttribute('data-ibc-qty-step'));
      if (next < 0) next = 0;
      setQuantity(stepContext, next);
      return;
    }

    var remove = target.closest('[data-ibc-cart-remove]');
    if (remove) {
      var removeContext = contextFor(remove);
      if (!removeContext) return;
      remove.setAttribute('aria-disabled', 'true');
      removeLine(removeContext);
      return;
    }

    var toggle = target.closest('[data-ibc-edit-toggle]');
    if (toggle) {
      var panelId = toggle.getAttribute('aria-controls');
      var panel = panelId ? document.getElementById(panelId) : null;
      if (!panel) return;
      var willOpen = panel.hidden;
      panel.hidden = !willOpen;
      toggle.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
      if (willOpen) {
        var first = panel.querySelector('input, textarea, button');
        if (first) first.focus();
      }
      return;
    }

    var cancel = target.closest('[data-ibc-edit-cancel]');
    if (cancel) {
      var wrapper = cancel.closest('.ibc-cart-edit__panel');
      if (!wrapper) return;
      wrapper.hidden = true;
      var owner = document.querySelector('[aria-controls="' + wrapper.id + '"]');
      if (owner) {
        owner.setAttribute('aria-expanded', 'false');
        owner.focus();
      }
      return;
    }

    var choiceSave = target.closest('[data-ibc-choice-save]');
    if (choiceSave) {
      var choiceWrap = choiceSave.closest('[data-ibc-choice-edit]');
      var choiceContext = contextFor(choiceSave);
      if (choiceWrap && choiceContext) saveChoice(choiceContext, choiceWrap);
      return;
    }

    var messageSave = target.closest('[data-ibc-message-save]');
    if (messageSave) {
      var messageWrap = messageSave.closest('[data-ibc-message-edit]');
      var messageContext = contextFor(messageSave);
      if (messageWrap && messageContext) saveMessage(messageContext, messageWrap);
      return;
    }

    var addonAdd = target.closest('[data-ibc-addon-add]');
    if (addonAdd) {
      var offer = addonAdd.closest('[data-ibc-addon-offer]');
      var offerContext = contextFor(addonAdd);
      if (offer && offerContext) addAddon(offerContext, offer);
    }
  });

  /* Typed quantities. `change` rather than `input`, so a partly typed number
   * never fires a request. */
  document.addEventListener('change', function (event) {
    var input = event.target;
    if (!input || !input.hasAttribute || !input.hasAttribute('data-ibc-qty-input')) return;
    if (input.disabled) return;
    var context = contextFor(input);
    if (!context) return;
    var value = parseInt(input.value, 10);
    if (isNaN(value) || value < 0) value = context.quantity;
    if (value === context.quantity) return;
    setQuantity(context, value);
  });

  /* When the chosen maximum is one, the checkboxes behave as radios. */
  document.addEventListener('change', function (event) {
    var input = event.target;
    if (!input || !input.hasAttribute) return;
    if (!input.hasAttribute('data-ibc-choice-input') && !input.hasAttribute('data-ibc-offer-flavour')) {
      return;
    }
    var scope = input.closest('[data-ibc-choice-edit], [data-ibc-addon-offer]');
    if (!scope) return;
    var max = parseInt(scope.getAttribute('data-max'), 10) || 0;
    if (max !== 1 || !input.checked) return;
    toArray(scope.querySelectorAll('[data-ibc-choice-input], [data-ibc-offer-flavour]')).forEach(
      function (other) {
        if (other !== input) other.checked = false;
      }
    );
  });

  /* Order note: debounce-saved on input, saved immediately on blur. */
  document.addEventListener('input', function (event) {
    var field = event.target;
    if (!field || !field.hasAttribute) return;

    if (field.hasAttribute('data-ibc-message-input')) {
      var counter = field.parentNode.querySelector('[data-ibc-message-count]');
      var limit = field.getAttribute('data-max-length');
      if (counter && limit) counter.textContent = field.value.length + '/' + limit;
      return;
    }

    if (!field.hasAttribute('data-ibc-cart-note')) return;
    var status = field.parentNode.querySelector('[data-ibc-note-status]');
    if (status) status.textContent = '';
    window.clearTimeout(noteTimer);
    noteTimer = window.setTimeout(function () {
      saveNote(field);
    }, 700);
  });

  document.addEventListener(
    'blur',
    function (event) {
      var field = event.target;
      if (!field || !field.hasAttribute || !field.hasAttribute('data-ibc-cart-note')) return;
      window.clearTimeout(noteTimer);
      saveNote(field);
    },
    true
  );

  /* Somebody else changed the basket — the product page, the quick-add
   * dialog or the add-on hook. Re-render from Liquid and show the drawer. */
  document.addEventListener('ibc:cart-changed', function (event) {
    var detail = event.detail || {};
    if (detail.source === 'cart') return;
    refresh().then(function () {
      if (!onCartPage()) openDrawer(null);
    });
  });

  /* Keep the busy overlay honest if a page is restored from bfcache. */
  window.addEventListener('pageshow', function () {
    setBusy(false, null);
  });

  window.IbcCart = {
    refresh: refresh,
    open: openDrawer,
    rebuildParentProperties: rebuildParent,
    reducedMotion: reducedMotion
  };
})();
